import cn from "classnames";
import { reportColumnAggregateTypesEnum } from "helpers/enums";
import { hiddenUserData } from "helpers/hiddenData";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import {
    columnName,
    formatValue,
    getStoredGroupByCurrentLogist,
    OVERALL_REPORT_VAT_TOGGLE_COLUMNS,
    setStoredGroupByCurrentLogist,
    typeSpecificProps,
} from "helpers/reports";
import compact from "lodash/compact";
import maxBy from "lodash/maxBy";
import mean from "lodash/mean";
import sum from "lodash/sum";
import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";
import { list as logistsList } from "sources/books/employees";
import CommonReport from "../commonReport";
import TruckBids from "./truckBids";

class OverallReport extends CommonReport {
    componentDidMount() {
        const carsCountWithActiveMileage = this.props.data.data.reduce(
            (acc, item) => (item.activeMileage > 0 ? acc + 1 : acc),
            0,
        );
        this.setState({ tableDefaultProps: this.tableDefaultProps(), carsCountWithActiveMileage });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.dataLoadedAt !== this.props.dataLoadedAt) {
            const carsCountWithActiveMileage = this.props.data.data.reduce(
                (acc, item) => (item.activeMileage > 0 ? acc + 1 : acc),
                0,
            );
            this.setState({ tableDefaultProps: this.tableDefaultProps(), carsCountWithActiveMileage });
        }
    }

    // TODO: remove this after fixing it in report data
    roundNegativeValue = (value) => (value >= -10 && value <= -1 ? 0 : value);

    setTableState = (data) => {
        const newLogistFilter = data.filters.find((item) => item.id === "carLogistId");
        const newLogistValue = newLogistFilter?.value?.value;
        const oldLogistFilter = this.state.filters.find((item) => item.id === "carLogistId");
        const oldLogistValue = oldLogistFilter?.value?.value;
        if (oldLogistValue !== newLogistValue) {
            this.props.getItem(newLogistValue ? { logistId: newLogistValue } : {});
        }
        this.setState(data);
    };

    cellProps = (column) => {
        return {
            Header: column.label,
            accessor: columnName(column),
            Cell: (props) => {
                const entity = props.row.values.car || props.row.values.trailer;
                const uniqueId = { [`data-${props.column.id}`.toLowerCase()]: entity };
                switch (props.column.id) {
                    case "car":
                    case "trailer":
                    case "counterpartyDisplayName": {
                        if (!props.row.original.bids?.length) {
                            return <span {...uniqueId}>{props.value}</span>;
                        }
                        const onClick = () => {
                            props.modal({
                                name: "infoModal",
                                windowMod: "wide",
                                title: props.value,
                                body: props.renderRowSubComponent(props.row),
                            });
                        };
                        return (
                            <div className="cursor-pointer" onClick={onClick} {...uniqueId}>
                                <i
                                    className={cn("r-item__expander", {
                                        "icon-uEAAE-angle-right-solid": !props.row.isExpanded,
                                        "icon-uEA61-angle-down": props.row.isExpanded,
                                    })}
                                />
                                <span {...uniqueId}>
                                    {props.column.id === "counterpartyDisplayName"
                                        ? hiddenUserData(props.value, { type: "name" })
                                        : props.value}
                                </span>
                            </div>
                        );
                    }
                    case "emptyMileageWithoutEmptyMileageBid": {
                        let { emptyMileageWithoutEmptyMileageBidRating } = props.row.original;
                        const { hasUndeterminedEmptyMileageWithoutBids } = props.row.original;
                        const value = this.roundNegativeValue(props.value);
                        if (value === 0) {
                            emptyMileageWithoutEmptyMileageBidRating = 0;
                        }
                        return (
                            <span {...uniqueId}>
                                {formatValue(value, column.clrType)} (
                                {formatValue(emptyMileageWithoutEmptyMileageBidRating / 100, "percent")})
                                {hasUndeterminedEmptyMileageWithoutBids && (
                                    <Tooltip overlay="Порожний пробег без заявок определён не полностью">
                                        <i className="pl-1 icon-uEA86-exclamation-circle text-warning" />
                                    </Tooltip>
                                )}
                            </span>
                        );
                    }
                    case "carLogistId": {
                        const { subRows } = props.row;
                        const logistName = subRows[0].original.carLogistDisplayName || "Без логиста";
                        const rowCount = /\(\d+\)$/.test(logistName) ? "" : ` (${subRows.length})`;

                        return (
                            <span {...uniqueId}>
                                {props.value === this.props.topLogistId && (
                                    <i className="icon-uEACC-crown report-crown-icon pr-1 text-brand" />
                                )}
                                {`${logistName}${rowCount}`}
                            </span>
                        );
                    }
                    case "emptyMileageBid": {
                        let { emptyMileageBidRating } = props.row.original;
                        const value = this.roundNegativeValue(props.value);
                        if (value === 0) {
                            emptyMileageBidRating = 0;
                        }
                        return (
                            <span {...uniqueId}>
                                {formatValue(value, column.clrType)} (
                                {formatValue(emptyMileageBidRating / 100, "percent")})
                            </span>
                        );
                    }
                    case "overallBidsPrice":
                    case "overallBidsPriceWithoutVat":
                    case "totalOverallBidsPrice":
                        return (
                            <span {...uniqueId}>
                                {hiddenUserData(formatValue(props.value, column.clrType), { type: "price" })}
                            </span>
                        );
                    case "output":
                    case "outputWithoutVat":
                    case "average":
                    case "averageWithoutVat":
                    case "rubPerHour":
                    case "outputPerHour":
                        return (
                            <span {...uniqueId}>
                                {hiddenUserData(formatValue(props.value, column.clrType), { type: "pricePerVal" })}
                            </span>
                        );
                    default: {
                        return <span {...uniqueId}>{formatValue(props.value, column.clrType)}</span>;
                    }
                }
            },
            Footer: () => {
                const defaultFooter = this.footerVal(column);
                const { footer } = this.props.data || {};
                switch (column.propertyName) {
                    case "EmptyMileageWithoutEmptyMileageBid": {
                        const { emptyMileageWithoutEmptyMileageBidRating } = footer;
                        return [
                            <span key="value">{defaultFooter}</span>,
                            <span key="rating">
                                {" "}
                                ({formatValue(emptyMileageWithoutEmptyMileageBidRating / 100, "percent")})
                            </span>,
                        ];
                    }
                    case "EmptyMileageBid": {
                        const { emptyMileageBidRating } = footer;
                        return [
                            <span key="value">{defaultFooter}</span>,
                            <span key="rating"> ({formatValue(emptyMileageBidRating / 100, "percent")})</span>,
                        ];
                    }
                    case "Car": {
                        return (
                            <>
                                <Tooltip overlay="Общее количество машин">
                                    <div className="ellipsis">
                                        {formatValue(footer.totalCarCount, "int")}{" "}
                                        <small>(Общее количество машин)</small>
                                    </div>
                                </Tooltip>
                                <Tooltip overlay="Количество активных машин">
                                    <div className="ellipsis">
                                        {formatValue(this.state.carsCountWithActiveMileage, "int")}{" "}
                                        <small>(Количество активных машин)</small>
                                    </div>
                                </Tooltip>
                            </>
                        );
                    }
                    case "Trailer": {
                        return (
                            <>
                                <Tooltip overlay="Общее количество прицепов">
                                    <div className="ellipsis">
                                        {formatValue(footer.totalTrailerCount, "int")}{" "}
                                        <small>(Общее количество прицепов)</small>
                                    </div>
                                </Tooltip>
                                <Tooltip overlay="Количество активных прицепов">
                                    <div className="ellipsis">
                                        {formatValue(this.state.carsCountWithActiveMileage, "int")}{" "}
                                        <small>(Количество активных прицепов)</small>
                                    </div>
                                </Tooltip>
                            </>
                        );
                    }
                    case "OverallBidsPrice":
                    case "OverallBidsPriceWithoutVat": {
                        return (
                            <>
                                <Tooltip overlay="Всего">
                                    <div className="ellipsis">
                                        {hiddenUserData(
                                            formatValue(
                                                this.props.overallReportWithNds
                                                    ? footer.totalOverallBidsPrice
                                                    : footer.totalOverallBidsPriceWithoutVat,
                                                "double",
                                            ),
                                            { type: "price" },
                                        )}{" "}
                                        <small>(Всего)</small>
                                    </div>
                                </Tooltip>
                                <Tooltip overlay="Сумма заявок с НДС">
                                    <div className="ellipsis">
                                        {hiddenUserData(formatValue(footer.overallBidsPrice, "double"), {
                                            type: "price",
                                        })}
                                        &nbsp;<small>(сумма заявок с НДС)</small>
                                    </div>
                                </Tooltip>
                                <Tooltip overlay="Сумма заявок без НДС">
                                    <div className="ellipsis">
                                        {hiddenUserData(formatValue(footer.overallBidsPriceWithoutVat, "double"), {
                                            type: "price",
                                        })}
                                        &nbsp;
                                        <small>(сумма заявок без НДС)</small>
                                    </div>
                                </Tooltip>
                            </>
                        );
                    }
                    case "OutputPerHour": {
                        const { outputPerHour } = footer;
                        return hiddenUserData(formatValue(outputPerHour, column.clrType), { type: "pricePerVal" });
                    }
                    case "Output":
                    case "OutputWithoutVat": {
                        return (
                            <>
                                <Tooltip overlay="Общий ВАЛ / общий пробег">
                                    <div className="ellipsis">
                                        {hiddenUserData(formatValue(footer.output, "double"), { type: "pricePerVal" })}
                                        &nbsp;<small>(с учетом НДС)</small>
                                    </div>
                                </Tooltip>
                                <Tooltip overlay="Общий ВАЛ без учета НДС / общий пробег">
                                    <div className="ellipsis">
                                        {hiddenUserData(formatValue(footer.totalBidsOutputWithoutVat, "double"), {
                                            type: "pricePerVal",
                                        })}
                                        &nbsp;<small>(без учета НДС)</small>
                                    </div>
                                </Tooltip>
                            </>
                        );
                    }
                    case "RubPerHour": {
                        return hiddenUserData(defaultFooter, { type: "pricePerVal" });
                    }
                    default:
                        return defaultFooter;
                }
            },
            ...typeSpecificProps(column),
            aggregate: (values) => {
                if (!this.numericClrTypes.includes(column.clrType)) {
                    return null;
                }

                if (["EmptyMileageBid", "EmptyMileageWithoutEmptyMileageBid"].includes(column.propertyName)) {
                    values = values.map(this.roundNegativeValue);
                }

                if (column.propertyName === "OutputPerHour") {
                    const aggregationKey = this.props.aggregationKey || "carLogistId";
                    const { carHours, overallBidsPrice } =
                        this.props.aggregatedData[values[0].original[aggregationKey]] || {};
                    return carHours ? overallBidsPrice / carHours : 0;
                }
                if (column.propertyName === "Output") {
                    const aggregationKey = this.props.aggregationKey || "carLogistId";
                    const { overallMileage, overallBidsPrice } =
                        this.props.aggregatedData[values[0].original[aggregationKey]] || {};
                    return overallMileage ? overallBidsPrice / overallMileage : 0;
                }
                if (column.propertyName === "OutputWithoutVat") {
                    const aggregationKey = this.props.aggregationKey || "carLogistId";
                    const { overallMileage, overallBidsPriceWithoutVat } =
                        this.props.aggregatedData[values[0].original[aggregationKey]] || {};
                    return overallMileage ? overallBidsPriceWithoutVat / overallMileage : 0;
                }
                switch (column.aggregateType) {
                    case reportColumnAggregateTypesEnum.Average:
                        return Math.round(mean(values));
                    case reportColumnAggregateTypesEnum.Sum:
                        return sum(values);
                    default:
                        return null;
                }
            },
            aggregateValue: (values, row) =>
                ["OutputPerHour", "Output", "OutputWithoutVat"].includes(column.propertyName) ? row : values,
            Aggregated: ({ value, row }) => {
                switch (column.propertyName) {
                    case "OutputWithoutVat":
                    case "Output":
                    case "RubPerHour":
                    case "OutputPerHour": {
                        return <strong>{hiddenUserData(formatValue(value, "double"), { type: "pricePerVal" })}</strong>;
                    }
                    case "EmptyMileageBid": {
                        const { overallMileage, emptyMileageBid } = row.values;
                        if (!overallMileage || !emptyMileageBid) {
                            return (
                                <strong>
                                    {formatValue(value, column.clrType)} ({formatValue(0, "percent")})
                                </strong>
                            );
                        }
                        return (
                            <strong>{`${formatValue(value, column.clrType)} (${formatValue(emptyMileageBid / overallMileage, "percent")})`}</strong>
                        );
                    }
                    case "EmptyMileageWithoutEmptyMileageBid": {
                        const { overallMileage, emptyMileageWithoutEmptyMileageBid } = row.values;
                        if (!overallMileage || !emptyMileageWithoutEmptyMileageBid) {
                            return (
                                <strong>
                                    {formatValue(value, column.clrType)} ({formatValue(0, "percent")})
                                </strong>
                            );
                        }
                        return (
                            <strong>
                                {`${formatValue(value, column.clrType)} (${formatValue(
                                    emptyMileageWithoutEmptyMileageBid / overallMileage,
                                    "percent",
                                )})`}
                            </strong>
                        );
                    }
                    case "OverallBidsPrice":
                    case "OverallBidsPriceWithoutVat":
                        return <strong>{hiddenUserData(formatValue(value, "double"), { type: "price" })}</strong>;
                    default:
                        return value !== null ? <strong>{formatValue(value, "double")}</strong> : "";
                }
            },
            ...(column.propertyName === "CarLogistId"
                ? {
                      Filter: reportFilterWrapper((props) => (
                          <SelectColumnFilter
                              {...props}
                              selectProps={{
                                  url: logistsList,
                                  inputProps: { name: "carLogistIds" },
                                  propName: "fullNameOrEmail",
                              }}
                          />
                      )),
                      filter: (rows, _id, filterValue) => {
                          if (!filterValue || !filterValue.value || filterValue.value.length === 0) {
                              return rows;
                          }
                          return rows.filter((row) => filterValue.value === row.values.carLogistId);
                      },
                  }
                : {}),
        };
    };

    extractColumns = () => {
        const { columns } = this.props.data || {};

        return compact(columns.map((column) => (column.label ? this.cellProps(column) : null)));
    };

    tableExtraProps = () => {
        const {
            overallReportWithNds,
            organization,
            profile: { availableFeatureModules },
        } = this.props;
        const { columns } = this.props.data || {};
        const hiddenColumns = columns
            .filter((column) => {
                return (
                    column.isAdditionalColumn ||
                    column.isFooterData ||
                    column.propertyName === "CarLogistDisplayName" ||
                    (overallReportWithNds
                        ? OVERALL_REPORT_VAT_TOGGLE_COLUMNS.withoutNds.includes(column.propertyName)
                        : OVERALL_REPORT_VAT_TOGGLE_COLUMNS.withNds.includes(column.propertyName))
                );
            })
            .map(columnName);

        return {
            initialState: {
                groupBy: ["carLogistId"],
                hiddenColumns,
            },
            renderRowSubComponent: (row, isPrintForm) => (
                <TruckBids
                    row={row}
                    isPrintForm={isPrintForm}
                    organization={organization}
                    availableFeatureModules={availableFeatureModules}
                />
            ),
        };
    };
}

export { OverallReport };

export default function ConfiguredReport(props) {
    const groupByCurrentLogist = props.groupByCurrentLogist ?? getStoredGroupByCurrentLogist();

    const toggleCheckbox = () => {
        const nextValue = !groupByCurrentLogist;
        setStoredGroupByCurrentLogist(nextValue);
        props.getItem({ groupByCurrentLogist: nextValue });
    };

    const checkboxCname = cn("checkbox d-inline-block mr-2", { "checkbox--checked": groupByCurrentLogist });

    const aggregatedData = useMemo(() => {
        const data = props.data.data.reduce((acc, item) => {
            if (!acc[item.carLogistId]) {
                acc[item.carLogistId] = {
                    carHours: 0,
                    overallBidsPrice: 0,
                    overallBidsPriceWithoutVat: 0,
                    overallMileage: 0,
                };
            }
            acc[item.carLogistId].carHours += item.carHours;
            acc[item.carLogistId].overallBidsPrice += item.overallBidsPrice;
            acc[item.carLogistId].overallBidsPriceWithoutVat += item.overallBidsPriceWithoutVat;
            acc[item.carLogistId].overallMileage += item.overallMileage;
            return acc;
        }, {});
        return data;
    }, [props.dataLoadedAt]);

    const topLogistId = () => {
        const logistKeys = Object.keys(aggregatedData).filter((key) => key !== "null");

        if (logistKeys.length > 1) {
            return Number(
                maxBy(Object.keys(aggregatedData), (logistId) => {
                    const { carHours, overallBidsPrice } = aggregatedData[logistId] || {};
                    return carHours ? overallBidsPrice / carHours : 0;
                }),
            );
        } else {
            return undefined;
        }
    };

    return (
        <>
            {!props.isPrintForm && (
                <div>
                    <div className="mb-2 btn" onClick={toggleCheckbox}>
                        <span className={checkboxCname} />
                        <small>Группировать машины по текущему логисту</small>
                    </div>
                </div>
            )}
            <OverallReport
                key={groupByCurrentLogist}
                {...props}
                getItem={(rest) => props.getItem({ groupByCurrentLogist, ...(rest ?? {}) })}
                groupByCurrentLogist={groupByCurrentLogist}
                aggregatedData={aggregatedData}
                topLogistId={topLogistId()}
            />
        </>
    );
}
